import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { managementAPI } from '../../services/api';
import api from '../../services/api';

function ManageDependents() {
  const [dependents, setDependents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Reassign modal
  const [reassignModal, setReassignModal] = useState({ open: false, dependent: null });
  const [selectedParentId, setSelectedParentId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  
  // Convert modal
  const [convertModal, setConvertModal] = useState({ open: false, dependent: null });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Load all users to get dependents and potential parents
      const usersResponse = await api.get('/management/users?status=all');
      const users = usersResponse.data.data.users || [];
      
      // Filter dependents
      const dependentUsers = users.filter(u => u.accountType === 'dependent');
      setDependents(dependentUsers);
      
      // Get potential parents (non-dependent users)
      const potentialParents = users.filter(u => u.accountType !== 'dependent');
      setAllUsers(potentialParents);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openReassignModal = (dependent) => {
    setReassignModal({ open: true, dependent });
    setSelectedParentId('');
    setError('');
    setSuccessMessage('');
  };

  const closeReassignModal = () => {
    setReassignModal({ open: false, dependent: null });
    setSelectedParentId('');
  };

  const handleReassign = async () => {
    if (!selectedParentId) {
      setError('Please select a new parent user');
      return;
    }

    if (!window.confirm(`Reassign ${reassignModal.dependent.displayName || reassignModal.dependent.username} to a new parent?`)) {
      return;
    }

    setReassigning(true);
    setError('');
    try {
      const response = await managementAPI.reassignDependent(
        reassignModal.dependent.id,
        selectedParentId
      );
      if (response.success) {
        setSuccessMessage(`Successfully reassigned ${reassignModal.dependent.displayName || reassignModal.dependent.username}`);
        closeReassignModal();
        loadData();
      } else {
        setError(response.error?.message || 'Failed to reassign');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reassign dependent');
    } finally {
      setReassigning(false);
    }
  };

  const openConvertModal = (dependent) => {
    setConvertModal({ open: true, dependent });
    setNewPassword('');
    setConfirmPassword('');
    setNewEmail('');
    setError('');
    setSuccessMessage('');
  };

  const closeConvertModal = () => {
    setConvertModal({ open: false, dependent: null });
    setNewPassword('');
    setConfirmPassword('');
    setNewEmail('');
  };

  const handleConvert = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm the new password');
      return;
    }

    if (!newEmail || !newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!window.confirm(
      `Convert ${convertModal.dependent.displayName || convertModal.dependent.username} to an independent account? ` +
      `This will remove their link to their parent account and allow them to log in independently with email ${newEmail}.`
    )) {
      return;
    }

    setConverting(true);
    setError('');
    try {
      const response = await managementAPI.convertDependentToIndependent(
        convertModal.dependent.id,
        newPassword,
        newEmail
      );
      if (response.success) {
        setSuccessMessage(`Successfully converted ${convertModal.dependent.displayName || convertModal.dependent.username} to independent account with email ${newEmail}`);
        closeConvertModal();
        loadData();
      } else {
        setError(response.error?.message || 'Failed to convert');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to convert dependent');
    } finally {
      setConverting(false);
    }
  };

  const getParentName = (parentId) => {
    const parent = allUsers.find(u => u.id === parentId);
    return parent ? (parent.displayName || parent.username) : 'Unknown';
  };

  if (loading) {
    return (
      <div className="container">
        <p>Loading dependent accounts...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div style={{ marginBottom: '2rem' }}>
        <Link to="/admin" style={{ color: '#2196F3', textDecoration: 'none' }}>
          ← Back to Admin Dashboard
        </Link>
      </div>

      <h2>Manage Dependent Accounts</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Reassign dependent accounts to different parents or convert them to independent accounts.
      </p>

      {error && (
        <div style={{
          background: '#ffebee',
          border: '1px solid #f44336',
          color: '#c62828',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          background: '#e8f5e9',
          border: '1px solid #4caf50',
          color: '#2e7d32',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {successMessage}
        </div>
      )}

      {dependents.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No dependent accounts found.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
          {dependents.map((dependent) => (
            <div
              key={dependent.id}
              className="card"
              style={{
                borderLeft: '4px solid #2196F3'
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem 0' }}>
                {dependent.displayName || dependent.username}
              </h3>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#666' }}>
                <strong>Username:</strong> {dependent.username}
              </p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#666' }}>
                <strong>Parent:</strong> {getParentName(dependent.parentUserId)}
              </p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#666' }}>
                <strong>Status:</strong> {dependent.status}
              </p>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                <button
                  onClick={() => openReassignModal(dependent)}
                  style={{
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                >
                  Reassign to Different Parent
                </button>
                <button
                  onClick={() => openConvertModal(dependent)}
                  style={{
                    background: '#ff9800',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                >
                  Convert to Independent Account
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reassign Modal */}
      {reassignModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>
              Reassign {reassignModal.dependent.displayName || reassignModal.dependent.username}
            </h3>
            <p style={{ color: '#666' }}>
              Current parent: <strong>{getParentName(reassignModal.dependent.parentUserId)}</strong>
            </p>

            <div style={{ marginTop: '1.5rem' }}>
              <label htmlFor="newParent" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Select New Parent:
              </label>
              <select
                id="newParent"
                value={selectedParentId}
                onChange={(e) => setSelectedParentId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              >
                <option value="">-- Select a user --</option>
                {allUsers
                  .filter(u => u.id !== reassignModal.dependent.parentUserId)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.displayName || user.username} ({user.email})
                    </option>
                  ))}
              </select>
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleReassign}
                disabled={reassigning || !selectedParentId}
                style={{
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: reassigning || !selectedParentId ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: reassigning || !selectedParentId ? 0.6 : 1
                }}
              >
                {reassigning ? 'Reassigning...' : 'Reassign'}
              </button>
              <button
                onClick={closeReassignModal}
                disabled={reassigning}
                style={{
                  background: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: reassigning ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {convertModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>
              Convert {convertModal.dependent.displayName || convertModal.dependent.username} to Independent
            </h3>
            <p style={{ color: '#666' }}>
              This will remove their link to their parent account and allow them to log in independently.
            </p>

            <div style={{ marginTop: '1.5rem' }}>
              <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                New Password (min 8 characters):
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Confirm Password:
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="newEmail" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                New Email Address:
              </label>
              <input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                Current email (parent's): {convertModal.dependent.email}
              </p>
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleConvert}
                disabled={converting || !newPassword || !confirmPassword || !newEmail}
                style={{
                  background: '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: converting || !newPassword || !confirmPassword || !newEmail ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: converting || !newPassword || !confirmPassword || !newEmail ? 0.6 : 1
                }}
              >
                {converting ? 'Converting...' : 'Convert'}
              </button>
              <button
                onClick={closeConvertModal}
                disabled={converting}
                style={{
                  background: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: converting ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageDependents;
